/**
 * Hub/Kapitel Navigation Module v4
 * Manages chapter navigation, intro/outro flow, and visited-tracking
 * Flow: Hero → Intro (scroll) → Hub → Kapitel (button) ↔ Hub (button) → Outro → Reset
 *
 * Mechanisms:
 *   A) Button-based view switching (StoryHubNav API + data-goto-hub / data-story-reset)
 *   B) Fade transitions via StoryAPI.fadeTo()
 *   C) View-Gating (only current view's panels get scroll-snap-align)
 *   D) Chapter-end scroll clamp (safety net for momentum overshoot)
 *   E) Visited-tracking + outro reset on hub arrival
 *
 * Data Attributes:
 *   data-intro="1"           - Intro panel (plays every reload)
 *   data-hub="1"             - Hub panel (main menu)
 *   data-chapter="id"        - Panel belongs to chapter
 *   data-chapter-start       - First panel of chapter
 *   data-chapter-end         - Last panel of chapter
 *   data-outro="1"           - Outro panel (after all chapters visited)
 *   data-goto-chapter="id"   - Button navigates to chapter
 *   data-goto-hub            - Button returns to hub (or outro if all visited)
 *   data-story-reset         - Button reloads the story
 *
 * Note: Uses IIFE pattern for Ghost's concatenated build system
 * Note: This file lives in assets/js/ → concatenated into source.js
 */

(function (window) {
  'use strict';

  var STORAGE_PREFIX = 'grenzwandler:hub:';
  var DEBUG = window.location.search.indexOf('debug=story') !== -1;

  function log() {
    if (DEBUG) {
      var args = ['[Hub]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  class StoryHub {
    constructor(core) {
      this.core = core;
      this.panels = [];

      // Structure maps
      this.hubIndices = [];
      this.hubIndex = -1;
      this.chapterIds = [];
      this.chapterStartIndex = {};
      this.introStartIndex = null;
      this.outroStartIndex = null;

      // State (memory only, reset per reload)
      this.introPlayed = false;
      this.outroPlayed = false;
      this.navTargetIndex = null;
      this.isNavigating = false;
      this.prevIndex = -1;
      this.currentlyInIntro = false;
      this.currentChapterId = null;

      // Chapter-end scroll clamp + accumulator
      this._chapterEndClampHandler = null;
      this._chapterEndTouchHandler = null;
      this._chapterEndScrollAccum = 0;
      this._chapterEndLastTime = 0;
      this._chapterEndTouchStartY = 0;

      // State (persisted)
      this.visited = {};

      this.init();
    }

    init() {
      if (!this.core || !this.core.root) return;

      // Get panels from StoryAPI if available, otherwise query DOM
      if (window.StoryAPI && window.StoryAPI.getPanels) {
        this.panels = window.StoryAPI.getPanels();
      } else {
        this.panels = Array.from(
          this.core.root.querySelectorAll('.story-panel')
        );
      }

      if (this.panels.length === 0) return;

      // Parse DOM structure
      this.parseStructure();

      // No hub found → no-op
      if (this.hubIndex === -1) {
        log('No hub panel found, module inactive.');
        return;
      }

      // Fallback: find outro panel if not detected by parseStructure.
      // The outro may be missing from ActivePanel's list (e.g. data-panel-disabled,
      // outside core.root, or Ghost editor issue). Search the entire document.
      if (this.outroStartIndex === null) {
        var docOutroPanels = document.querySelectorAll('.story-panel[data-outro]');

        if (docOutroPanels.length > 0) {
          var outroEl = docOutroPanels[0];
          var existingIdx = this.panels.indexOf(outroEl);

          if (existingIdx !== -1) {
            // Panel IS in list but parseStructure missed it (shouldn't happen)
            this.outroStartIndex = existingIdx;
            log('Outro found in panel list at index', existingIdx, '(missed during parse)');
          } else {
            // Panel NOT in list — add it and register with ActivePanel
            this.panels.push(outroEl);
            this.outroStartIndex = this.panels.length - 1;

            // Ensure visibility: PanelReveal didn't observe this panel,
            // so data-reveal keeps it at opacity:0. Force is-visible.
            outroEl.classList.add('is-visible');

            // Register with ActivePanel's observer so fadeTo works
            var apModule = this.core.modules.get('activePanel');
            if (apModule) {
              apModule.panelRatios.set(outroEl, 0);
              if (apModule.observer) {
                apModule.observer.observe(outroEl);
              }
            }

            log('Outro panel was outside panel list — force-added at index', this.outroStartIndex);
          }
        } else {
          log('WARNING: No panel with data-outro found in document.',
              'Make sure the outro section is in a Ghost HTML card with data-outro="1".');
        }
      }

      // Restore visited state from localStorage
      this.restoreState();

      // Expose View-Gate API for fadeTo
      var self = this;
      window.StoryViewGate = {
        prepareFor: function(targetIndex) { self.gateForPanel(targetIndex); }
      };

      // Expose Hub Navigation API
      window.StoryHubNav = {
        returnToHub: function() {
          log('returnToHub check:', {
            chapters: self.chapterIds,
            visited: self.visited,
            allVisited: self.allChaptersVisited(),
            outroStartIndex: self.outroStartIndex,
            outroPlayed: self.outroPlayed
          });
          if (self.allChaptersVisited() && self.outroStartIndex !== null && !self.outroPlayed) {
            self.outroPlayed = true;
            log('StoryHubNav: all chapters visited, navigating to outro.');
            self.navigateTo(self.outroStartIndex);
          } else {
            log('StoryHubNav: returning to hub.');
            self.navigateTo(self.hubIndex);
          }
        },
        resetStory: function() {
          log('StoryHubNav: resetting story.');
          self.visited = {};
          self.saveState();
          window.location.reload();
        }
      };

      // Initial view gate (intro view)
      this.gateForPanel(0);

      // Setup event listeners
      this.setupListeners();

      // Setup hub button click handlers
      this.setupNavigation();

      // Setup return/reset buttons
      this.setupReturnButtons();

      // Initial UI update
      this.updateHubUI(null);

      // Setup hub background image tracking (pin layer sizing)
      this._setupHubBgTracking();

      // Register with core
      this.core.registerModule('hub', this);

      // Diagnostic: show panel count discrepancies
      var docPanelCount = document.querySelectorAll('.story-panel').length;
      if (docPanelCount !== this.panels.length) {
        log('Panel count mismatch: ActivePanel has', this.panels.length,
            'but document has', docPanelCount, '.story-panel elements');
      }

      log('Initialized:', {
        hubIndex: this.hubIndex,
        chapters: this.chapterIds,
        chapterStartIndex: this.chapterStartIndex,
        introStartIndex: this.introStartIndex,
        outroStartIndex: this.outroStartIndex,
        panelCount: this.panels.length,
        visited: this.visited
      });
    }

    /* ----------------------------------------------------------
       Hub Background Image Tracking
       Sizes .hub-pin-layer to match the hub-bg <img> contain-fit
       geometry so pin percentages map to image content, not panel.
       Pattern from landing-map.js resizeCanvas().
       ---------------------------------------------------------- */

    _setupHubBgTracking() {
      var self = this;

      this.hubIndices.forEach(function (hubIdx) {
        var hubPanel = self.panels[hubIdx];
        if (!hubPanel) return;

        var hubBg = hubPanel.querySelector('.hub-bg');
        var pinLayer = hubPanel.querySelector('.hub-pin-layer');
        if (!hubBg || !pinLayer) return;

        // Store references for resize
        if (!self._hubBgRefs) self._hubBgRefs = [];
        self._hubBgRefs.push({ panel: hubPanel, img: hubBg, pinLayer: pinLayer });

        // Initial sizing (after image loads)
        if (hubBg.complete && hubBg.naturalWidth) {
          self._resizeHubPinLayer(hubPanel, hubBg, pinLayer);
        } else {
          hubBg.addEventListener('load', function () {
            self._resizeHubPinLayer(hubPanel, hubBg, pinLayer);
          });
        }

        // ResizeObserver for viewport changes
        if (window.ResizeObserver) {
          var ro = new ResizeObserver(function () {
            self._resizeHubPinLayer(hubPanel, hubBg, pinLayer);
          });
          ro.observe(hubPanel);
          if (!self._hubResizeObservers) self._hubResizeObservers = [];
          self._hubResizeObservers.push(ro);
        }
      });

      // Fallback: debounced resize for browsers without ResizeObserver
      if (!window.ResizeObserver && this._hubBgRefs && this._hubBgRefs.length) {
        var timer;
        var self2 = this;
        this._hubResizeHandler = function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            self2._hubBgRefs.forEach(function (ref) {
              self2._resizeHubPinLayer(ref.panel, ref.img, ref.pinLayer);
            });
          }, 100);
        };
        window.addEventListener('resize', this._hubResizeHandler);
      }
    }

    _resizeHubPinLayer(panel, img, pinLayer) {
      if (!img.naturalWidth || !img.naturalHeight) return;

      var panelW = panel.clientWidth;
      var panelH = panel.clientHeight;
      var imgRatio = img.naturalWidth / img.naturalHeight;
      var panelRatio = panelW / panelH;
      var w, h;

      // Contain geometry: fit image inside panel, preserving aspect ratio
      if (imgRatio > panelRatio) {
        // Image wider than panel → letterbox top/bottom
        w = panelW;
        h = panelW / imgRatio;
      } else {
        // Image taller → letterbox left/right
        h = panelH;
        w = panelH * imgRatio;
      }

      var left = (panelW - w) / 2;
      var top = (panelH - h) / 2;

      pinLayer.style.width = w + 'px';
      pinLayer.style.height = h + 'px';
      pinLayer.style.left = left + 'px';
      pinLayer.style.top = top + 'px';

      log('Pin layer resized:', Math.round(w) + 'x' + Math.round(h),
          'at', Math.round(left) + ',' + Math.round(top));
    }

    parseStructure() {
      var self = this;
      var seenChapterStarts = {};

      this.panels.forEach(function (panel, index) {
        // Hub panels
        if (panel.hasAttribute('data-hub')) {
          self.hubIndices.push(index);
        }

        // Intro panels
        if (panel.hasAttribute('data-intro')) {
          if (self.introStartIndex === null) {
            self.introStartIndex = index;
          }
        }

        // Outro panels
        if (panel.hasAttribute('data-outro')) {
          if (self.outroStartIndex === null) {
            self.outroStartIndex = index;
          }
        }

        // Chapter panels
        var chapterId = panel.dataset.chapter;
        if (chapterId) {
          if (self.chapterIds.indexOf(chapterId) === -1) {
            self.chapterIds.push(chapterId);
          }

          if (panel.hasAttribute('data-chapter-start')) {
            if (seenChapterStarts[chapterId]) {
              console.warn('[Hub] Duplicate data-chapter-start for chapter "' + chapterId + '" at panel ' + index);
            } else {
              seenChapterStarts[chapterId] = true;
              self.chapterStartIndex[chapterId] = index;
            }
          }

          if (typeof self.chapterStartIndex[chapterId] === 'undefined') {
            self.chapterStartIndex[chapterId] = index;
          }
        }
      });

      this.hubIndex = this.hubIndices.length > 0 ? this.hubIndices[0] : -1;

      // Warnings
      var self2 = this;
      this.chapterIds.forEach(function (id) {
        if (!seenChapterStarts[id]) {
          console.warn('[Hub] Chapter "' + id + '" has no data-chapter-start. Using first occurrence at panel ' + self2.chapterStartIndex[id]);
        }
      });

      if (this.hubIndex !== -1) {
        var hubPanel = this.panels[this.hubIndex];
        var buttons = hubPanel.querySelectorAll('[data-goto-chapter]');
        buttons.forEach(function (btn) {
          var targetId = btn.dataset.gotoChapter;
          if (typeof self2.chapterStartIndex[targetId] === 'undefined') {
            console.warn('[Hub] Button data-goto-chapter="' + targetId + '" has no matching chapter.');
          }
        });
      }
    }

    // --- Storage ---

    restoreState() {
      try {
        var key = STORAGE_PREFIX + window.location.pathname;
        var stored = window.localStorage.getItem(key);
        if (stored) {
          var data = JSON.parse(stored);
          if (data && data.visited) {
            this.visited = data.visited;
          }
        }
      } catch (e) {
        console.warn('[Hub] Could not restore state:', e.message);
      }
    }

    saveState() {
      try {
        var key = STORAGE_PREFIX + window.location.pathname;
        window.localStorage.setItem(key, JSON.stringify({ visited: this.visited }));
      } catch (e) {
        console.warn('[Hub] Could not save state:', e.message);
      }
    }

    // ===========================================
    // VIEW-GATING
    // Only panels in the current "view" get scroll-snap-align
    // ===========================================

    setViewGate(enabledIndices) {
      this.panels.forEach(function(panel, index) {
        var enabled = enabledIndices.indexOf(index) !== -1;

        // SplitScroll: panel always snap=none (set during init).
        // Gate the STEPS instead — they are the actual snap targets.
        if (panel.getAttribute('data-story-type') === 'splitscroll') {
          var steps = panel.querySelectorAll('.splitscroll__step');
          for (var i = 0; i < steps.length; i++) {
            steps[i].style.scrollSnapAlign = enabled ? '' : 'none';
          }
          return;
        }

        panel.style.scrollSnapAlign = enabled ? '' : 'none';
      });
      log('View gate:', enabledIndices);
    }

    gateForPanel(panelIndex) {
      var panel = this.panels[panelIndex];
      if (!panel) return;
      var indices = [];
      var self = this;

      // Panels before the hub that aren't chapters/outro belong to the intro view,
      // even without an explicit data-intro attribute.
      var isIntroView = panel.hasAttribute('data-intro') || panel.hasAttribute('data-hub');
      if (!isIntroView && this.hubIndex !== -1 && panelIndex <= this.hubIndex
          && !panel.dataset.chapter && !panel.hasAttribute('data-outro')) {
        isIntroView = true;
      }

      if (isIntroView) {
        // Intro/Hub View — all panels from start up to and including the hub
        for (var i = 0; i <= this.hubIndex; i++) {
          indices.push(i);
        }
      } else if (panel.dataset.chapter) {
        // Chapter View — only panels of same chapter
        var chId = panel.dataset.chapter;
        this.panels.forEach(function(p, i) {
          if (p.dataset.chapter === chId) indices.push(i);
        });
      } else if (panel.hasAttribute('data-outro')) {
        // Outro View — only outro panel
        indices.push(panelIndex);
      } else {
        // Fallback: all panels (safe)
        this.panels.forEach(function(p, i) { indices.push(i); });
      }

      this.setViewGate(indices);
    }

    // ===========================================
    // CHAPTER-END SCROLL CLAMP (safety net)
    // Prevents momentum overshoot past chapter boundary
    // ===========================================

    updateChapterEndClamp(panel) {
      // Clean up previous handlers
      if (this._chapterEndClampHandler) {
        document.removeEventListener('wheel', this._chapterEndClampHandler, { capture: true });
        this._chapterEndClampHandler = null;
        log('Chapter-end clamp removed');
      }
      if (this._chapterEndTouchHandler) {
        document.removeEventListener('touchstart', this._chapterEndTouchHandler, { capture: true });
        document.removeEventListener('touchmove', this._chapterEndTouchHandler, { capture: true });
        this._chapterEndTouchHandler = null;
      }
      this._chapterEndScrollAccum = 0;

      // Install clamp + accumulator only on chapter-end panels
      if (!panel.hasAttribute('data-chapter-end')) return;

      var self = this;
      var isMobile = window.innerWidth <= 768;
      var threshold = window.innerHeight * (isMobile ? 0.6 : 1.0);

      // Wheel: block down-scroll + accumulate toward threshold
      this._chapterEndClampHandler = function(e) {
        if (e.deltaY <= 0) return;

        // Multi-viewport panels (e.g. SplitScroll): allow scrolling until
        // the user reaches the bottom of the panel
        var panelRect = panel.getBoundingClientRect();
        if (panelRect.bottom > window.innerHeight + 50) {
          return; // Panel extends below viewport — let user scroll through
        }

        e.preventDefault();

        var now = Date.now();
        // Reset accumulator after 500ms pause (new gesture)
        if (now - self._chapterEndLastTime > 500) {
          self._chapterEndScrollAccum = 0;
        }
        self._chapterEndLastTime = now;

        self._chapterEndScrollAccum += e.deltaY;
        if (DEBUG && self._chapterEndScrollAccum > threshold * 0.3) {
          log('Chapter-end scroll: ' + Math.round(self._chapterEndScrollAccum) + '/' + Math.round(threshold));
        }
        if (self._chapterEndScrollAccum >= threshold) {
          self._chapterEndScrollAccum = 0;
          log('Chapter-end scroll threshold reached, returning to hub');
          window.StoryHubNav.returnToHub();
        }
      };

      // Touch: block down-swipe + accumulate
      this._chapterEndTouchHandler = function(e) {
        if (e.type === 'touchstart') {
          self._chapterEndTouchStartY = e.touches[0].clientY;
          return;
        }
        // touchmove
        var deltaY = self._chapterEndTouchStartY - e.touches[0].clientY;
        if (deltaY <= 0) return;

        // Multi-viewport panels: allow scrolling until bottom reached
        var panelRect = panel.getBoundingClientRect();
        if (panelRect.bottom > window.innerHeight + 50) {
          return;
        }

        e.preventDefault();

        self._chapterEndScrollAccum += deltaY;
        self._chapterEndTouchStartY = e.touches[0].clientY;

        if (self._chapterEndScrollAccum >= threshold) {
          self._chapterEndScrollAccum = 0;
          log('Chapter-end touch threshold reached, returning to hub');
          window.StoryHubNav.returnToHub();
        }
      };

      document.addEventListener('wheel', this._chapterEndClampHandler, { passive: false, capture: true });
      document.addEventListener('touchstart', this._chapterEndTouchHandler, { passive: true, capture: true });
      document.addEventListener('touchmove', this._chapterEndTouchHandler, { passive: false, capture: true });
      log('Chapter-end clamp installed (threshold: ' + Math.round(threshold) + 'px)');
    }

    // ===========================================
    // NAVIGATION
    // ===========================================

    setupNavigation() {
      var self = this;

      this.hubIndices.forEach(function (hubIdx) {
        var hubPanel = self.panels[hubIdx];
        var buttons = hubPanel.querySelectorAll('[data-goto-chapter]');

        buttons.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var chapterId = btn.dataset.gotoChapter;

            if (typeof self.chapterStartIndex[chapterId] === 'undefined') {
              console.warn('[Hub] Cannot navigate: no chapter "' + chapterId + '" found.');
              return;
            }

            log('Button clicked: chapter "' + chapterId + '"');
            self.navigateTo(self.chapterStartIndex[chapterId]);
          });
        });
      });
    }

    setupReturnButtons() {
      var self = this;
      // "Zurück zum Hub" buttons (in chapter panels) — idempotent
      document.querySelectorAll('[data-goto-hub]').forEach(function(btn) {
        if (btn.dataset.hubBound) return;
        btn.dataset.hubBound = '1';
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          log('Return button clicked');
          window.StoryHubNav.returnToHub();
        });
      });
      // "Story neu laden" button (in outro panel) — idempotent
      document.querySelectorAll('[data-story-reset]').forEach(function(btn) {
        if (btn.dataset.hubBound) return;
        btn.dataset.hubBound = '1';
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          log('Story reset clicked');
          window.StoryHubNav.resetStory();
        });
      });
    }

    navigateTo(targetIndex) {
      // Guard: prevent double navigation during fade
      if (this.isNavigating) {
        log('navigateTo blocked: already navigating');
        return;
      }

      // Set navigation guards
      this.navTargetIndex = targetIndex;
      this.isNavigating = true;

      var self = this;

      // Prefer fadeTo for smooth transitions
      if (window.StoryAPI && window.StoryAPI.fadeTo) {
        log('Navigating (fade) to panel ' + targetIndex);

        // Safety timeout: if fadeTo callback never fires, clear guard
        var navSafetyTimer = setTimeout(function() {
          if (self.isNavigating) {
            log('navigateTo safety timeout: clearing isNavigating guard');
            self.isNavigating = false;
            self.navTargetIndex = null;
          }
        }, 3000);

        window.StoryAPI.fadeTo(targetIndex, function () {
          clearTimeout(navSafetyTimer);
          // Only clear isNavigating; leave navTargetIndex for the
          // IntersectionObserver to clear when the target panel activates.
          // Use a delayed safety net (2s) in case observer never fires.
          self.isNavigating = false;

          if (self.navTargetIndex === targetIndex) {
            setTimeout(function () {
              if (self.navTargetIndex === targetIndex) {
                log('fadeTo safety timeout: clearing navTargetIndex');
                self.navTargetIndex = null;
              }
            }, 2000);
          }
        });
      } else if (window.StoryAPI && window.StoryAPI.goto) {
        // Fallback to regular goto
        log('Navigating (goto fallback) to panel ' + targetIndex);
        window.StoryAPI.goto(targetIndex);
        self.isNavigating = false;
      } else {
        console.warn('[Hub] StoryAPI not available.');
        self.isNavigating = false;
        self.navTargetIndex = null;
      }
    }

    // ===========================================
    // EVENT HANDLING
    // ===========================================

    setupListeners() {
      var self = this;
      document.addEventListener('story:panel:activated', function (e) {
        self.handlePanelActivated(e.detail);
      });
    }

    handlePanelActivated(detail) {
      var panel = detail.panel;
      var index = detail.index;

      // Guard: panel may be undefined if index is outside hub's panel list
      if (!panel) return;

      // ---- VIEW GATE: update snap targets for current view ----
      this.gateForPanel(index);

      // ---- GUARD: skip intermediate panels during programmatic navigation ----
      if (this.navTargetIndex !== null) {
        if (index === this.navTargetIndex) {
          // Reached target - clear guard and process
          log('Reached nav target: panel ' + index);
          this.navTargetIndex = null;
          this.prevIndex = index;

          // Process the target panel
          this.processTargetPanel(panel, index);
          return;
        } else {
          // Intermediate panel - skip entirely
          this.prevIndex = index;
          return;
        }
      }

      // ---- INTRO TRACKING ----
      var isIntroPanel = panel.hasAttribute('data-intro');
      var wasInIntro = this.currentlyInIntro;
      this.currentlyInIntro = isIntroPanel;

      if (wasInIntro && !isIntroPanel && !this.introPlayed) {
        this.introPlayed = true;
        log('Intro completed, navigating to hub.');

        if (index !== this.hubIndex) {
          this.prevIndex = index;
          this.navigateTo(this.hubIndex);
          return;
        }
        // If already at hub, fall through to process normally
      }

      // ---- HUB HANDLING ----
      if (panel.hasAttribute('data-hub')) {
        // Reset on hub arrival after outro
        if (this.outroPlayed) {
          this.visited = {};
          this.saveState();
          log('At hub from outro: visited + outroPlayed reset');
        }
        this.outroPlayed = false;
      }

      // ---- CHAPTER TRACKING ----
      var chapterId = panel.dataset.chapter || null;
      if (chapterId) {
        // Only mark visited when reaching chapter-end (fully viewed)
        if (!this.visited[chapterId] && panel.hasAttribute('data-chapter-end')) {
          this.visited[chapterId] = true;
          this.saveState();
          log('Chapter "' + chapterId + '" visited (reached end).');
        }
        this.currentChapterId = chapterId;
      } else {
        this.currentChapterId = null;
      }

      // ---- CHAPTER-END SCROLL CLAMP (safety net) ----
      this.updateChapterEndClamp(panel);

      // ---- UPDATE HUB UI ----
      this.updateHubUI(this.currentChapterId);

      // Update previous index
      this.prevIndex = index;
    }

    // Process a panel that was the target of programmatic navigation
    processTargetPanel(panel, index) {
      // View gate for target panel
      this.gateForPanel(index);

      // Hub handling
      if (panel.hasAttribute('data-hub')) {
        // Only reset visited when returning FROM outro (outroPlayed was true)
        if (this.outroPlayed) {
          this.visited = {};
          this.saveState();
          log('At hub (nav target) from outro: visited + outroPlayed reset');
        }
        this.outroPlayed = false;
      }

      // Chapter tracking — only mark visited at chapter-end
      var chapterId = panel.dataset.chapter || null;
      if (chapterId) {
        if (!this.visited[chapterId] && panel.hasAttribute('data-chapter-end')) {
          this.visited[chapterId] = true;
          this.saveState();
          log('Chapter "' + chapterId + '" visited (reached end).');
        }
        this.currentChapterId = chapterId;
      } else {
        this.currentChapterId = null;
      }

      // Chapter-end scroll clamp (safety net)
      this.updateChapterEndClamp(panel);

      this.updateHubUI(this.currentChapterId);
    }

    allChaptersVisited() {
      var self = this;
      return this.chapterIds.every(function (id) {
        return self.visited[id] === true;
      });
    }

    // ===========================================
    // HUB UI
    // ===========================================

    updateHubUI(currentChapterId) {
      var self = this;
      var visitedCount = 0;

      this.chapterIds.forEach(function (id) {
        if (self.visited[id]) visitedCount++;
      });

      this.hubIndices.forEach(function (hubIdx) {
        var hubPanel = self.panels[hubIdx];
        hubPanel.setAttribute('data-hub-state', visitedCount + '/' + self.chapterIds.length);

        // Search for chapter buttons in hub panel AND in overlay-pin layer
        // (overlay-hub moves pins out of the hub panel into a fixed container)
        var buttons = hubPanel.querySelectorAll('[data-goto-chapter]');
        var overlayPins = document.querySelector('.hub-overlay-pins');
        if (overlayPins) {
          var extraBtns = overlayPins.querySelectorAll('[data-goto-chapter]');
          buttons = Array.prototype.slice.call(buttons).concat(Array.prototype.slice.call(extraBtns));
        }
        (Array.isArray(buttons) ? buttons : Array.prototype.slice.call(buttons)).forEach(function (btn) {
          var id = btn.dataset.gotoChapter;

          if (self.visited[id]) {
            btn.classList.add('is-visited');
          } else {
            btn.classList.remove('is-visited');
          }

          if (id === currentChapterId) {
            btn.classList.add('is-current');
          } else {
            btn.classList.remove('is-current');
          }
        });
      });

      document.dispatchEvent(new CustomEvent('story:hub:updated', {
        detail: { visitedCount: visitedCount, totalChapters: this.chapterIds.length }
      }));
    }

    destroy() {
      // Cleanup chapter-end clamp
      if (this._chapterEndClampHandler) {
        document.removeEventListener('wheel', this._chapterEndClampHandler, { capture: true });
        this._chapterEndClampHandler = null;
      }
      if (this._chapterEndTouchHandler) {
        document.removeEventListener('touchstart', this._chapterEndTouchHandler, { capture: true });
        document.removeEventListener('touchmove', this._chapterEndTouchHandler, { capture: true });
        this._chapterEndTouchHandler = null;
      }
      if (window.StoryViewGate) {
        window.StoryViewGate = null;
      }
      if (window.StoryHubNav) {
        window.StoryHubNav = null;
      }
      this.panels = [];
      this.hubIndices = [];
      this.chapterIds = [];
      this.chapterStartIndex = {};
      this.visited = {};
    }
  }

  window.StoryModuleHub = StoryHub;

})(window);
